import { z } from "zod";

const optional = z
  .string()
  .trim()
  .min(1)
  .nullish()
  .or(z.literal("").transform(() => null));

export const saveCatalogItemInput = z.object({
  itemId: z.string().uuid().optional(),
  confirmationId: z.string().optional(),

  category: z.string().trim().min(1, "La catégorie est requise"),
  subcategory: optional,
  name: z.string().trim().min(1, "Le libellé est requis"),
  unit: z.string().trim().min(1, "L'unité est requise"),

  unitCost: z.coerce.number().min(0, "Le coût ne peut pas être négatif"),
  /** Vide = on reprend le prix de revient. */
  unitPrice: z.preprocess(
    (v) => (v === "" || v === null || v === undefined ? undefined : Number(v)),
    z.number().min(0).optional(),
  ),
  tvaRate: z.coerce.number().min(0).max(100),
  supplierId: z.string().uuid().nullish(),

  /** Vide = stock non suivi pour cet article. */
  stockQuantity: z.preprocess(
    (v) => (v === "" || v === null || v === undefined ? undefined : Number(v)),
    z.number().min(0).optional(),
  ),
  /** Vide = pas d'alerte de réapprovisionnement pour cet article. */
  reorderThreshold: z.preprocess(
    (v) => (v === "" || v === null || v === undefined ? undefined : Number(v)),
    z.number().min(0).optional(),
  ),
});

export type SaveCatalogItemInput = z.infer<typeof saveCatalogItemInput>;

export const listCatalogInput = z.object({
  confirmationId: z.string().optional(),
  category: z.string().optional(),
  search: z.string().optional(),
});

export type ListCatalogInput = z.infer<typeof listCatalogInput>;

/** Import CSV. Une ligne invalide ne doit pas faire échouer tout le fichier. */
export const importCatalogInput = z.object({
  confirmationId: z.string().optional(),
  items: z
    .array(saveCatalogItemInput.omit({ itemId: true, confirmationId: true }))
    .min(1, "Le fichier ne contient aucune ligne exploitable"),
});

export type ImportCatalogInput = z.infer<typeof importCatalogInput>;

/**
 * Sortir une quantité du stock pour un chantier.
 *
 * Le chantier est **requis**, et c'est tout le sens de la règle choisie : une
 * sortie sans chantier ne serait qu'un ajustement d'inventaire, et personne ne
 * saurait à quelle marge l'imputer.
 */
export const withdrawStockInput = z.object({
  confirmationId: z.string().optional(),
  itemId: z.string().uuid(),
  projectId: z.string().uuid("Choisissez le chantier qui l'a consommé"),
  quantity: z.coerce.number().positive("La quantité sortie doit être positive"),
  note: optional,
});

export type WithdrawStockInput = z.infer<typeof withdrawStockInput>;

export const listProjectStockUsageInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid(),
});

export type ListProjectStockUsageInput = z.infer<typeof listProjectStockUsageInput>;
